import React, { useCallback, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import useFormatters from '../../hooks/useFormatters';

import SimulationServiceTypeSelect from '../Simulation/SimulationServiceTypeSelect';
import useRateSheetInput from '../../hooks/useRateSheetInput';

export default function RateSheet({ handleAPIRequest, setChatBotContext }) {

    const [alertError, setAlertError] = useState(null);

    const {
        setRateSheetInput
        , rateSheetInput
        , onRateSheetReset
        , currentVersion
    } = useRateSheetInput();

    const setAdultPediatric = (adultPediatric, serviceType) => {
        if (adultPediatric && serviceType) {
            setRateSheetInput({
                adultPediatric
                , serviceTypeCategory: rateSheetInput.serviceTypeCategory
                , serviceType
            });
        }
        else if (adultPediatric) {
            setRateSheetInput({
                adultPediatric
                , serviceTypeCategory: rateSheetInput.serviceTypeCategory
                , serviceType: rateSheetInput.serviceType
            });
        }
        else {
            setRateSheetInput({
                adultPediatric: 'A'
                , serviceTypeCategory: rateSheetInput.serviceTypeCategory
                , serviceType: rateSheetInput.serviceType
            });
        }
    };

    const setServiceTypeCategory = (serviceTypeCategory) => {
        if (serviceTypeCategory) {
            setRateSheetInput({
                adultPediatric: rateSheetInput.adultPediatric
                , serviceTypeCategory
                , serviceType: null
            });
            setNetworks([]);
        }
        else {
            setRateSheetInput({
                adultPediatric: rateSheetInput.adultPediatric
                , serviceTypeCategory: null
                , serviceType: null
            });
            setNetworks([]);
        }
    };

    const setServiceType = (serviceType) => {
        if (serviceType) {
            setRateSheetInput({
                adultPediatric: rateSheetInput.adultPediatric
                , serviceTypeCategory: rateSheetInput.serviceTypeCategory
                , serviceType
            });
        }
        else {
            setRateSheetInput({
                adultPediatric: rateSheetInput.adultPediatric
                , serviceTypeCategory: rateSheetInput.serviceTypeCategory
                , serviceType: null
            });
            setNetworks([]);
        }
    };

    const handleOnRateSheetReset = () => {
        onRateSheetReset();
        setNetworks([]);
    };

    const [networks, setNetworks] = useState([]);
    const [networksTextFilter, setNetworksTextFilter] = useState('');

    const { provider_id, network_id, service_type_id } = useParams();

    useEffect(() => {

        if (networks.length === 1) {

            let new_chat_bot_context = "";

            new_chat_bot_context += `For Service Type ${networks[0]['service_type']}, Provider ${networks[0]['provider']} with Network ${networks[0]['network']} has the following Contract Information: Administration Case Rate: ${networks[0]['admin_case_rate']}; Facility Rate: ${networks[0]['facility_pct_charge']}; Biologic Rate as a % of WAC: ${networks[0]['biologic_rate_pct_wac']}; Outlier Charge Threshold: ${networks[0]['outlier_threshold']}; Outlier % Charge: ${networks[0]['outlier_pct_charge']}; Minimum Payment Provision: ${networks[0]['mpp_pct_charge']}; Inlier Days: ${networks[0]['days_included']}; and Per Diem: ${networks[0]['per_diem_rate']}.`;

            setChatBotContext(new_chat_bot_context);
            console.log('setting chat bot context...' + JSON.stringify(new_chat_bot_context))
        }

    }, [networks]);

    const fetchNetworks = useCallback(() => {

        const do_fetch = async () => {

            const new_search_service_id = service_type_id ? service_type_id : (rateSheetInput.serviceType ? rateSheetInput.serviceType['service_type_id'] : null);

            const fff = {
                service_type_id: new_search_service_id
            };

            let fetch_url = '/api/v1/networks?' + new URLSearchParams(fff);

            console.log('fetching networks: ' + fetch_url);

            const res = await handleAPIRequest(fetch_url);
            const data = await res.json();

            if (res.status === 200) {
                if (Array.isArray(data['networks'])) {

                    const _networks = data['networks'].filter((n) => {
                        if (provider_id && network_id && service_type_id)
                            return n['provider_id'] === parseInt(provider_id) && n['network_id'] === parseInt(network_id) && n['service_type_id'] === parseInt(service_type_id);
                        else if (rateSheetInput.serviceType) {
                            return n['service_type_id'] === parseInt(rateSheetInput.serviceType['service_type_id']);
                        }
                        else
                            return true;
                    })

                    // const provider_ids = [...new Set(_networks.map((n) => n['provider_id']))];

                    console.log('loaded networks for ' + JSON.stringify(fff) + ' : ' + data['networks'].length);

                    try {

                        setNetworks(_networks.map((n) => {
                            n['name_full'] = `${n['provider']} : ${n['network']} : ${n['service_type']}`;
                            return n;
                        }).sort((a, b) => {
                            if (a.name_full < b.name_full)
                                return -1;
                            else if (a.name_full > b.name_full)
                                return 1;
                            else
                                return 0;
                        }));

                    } catch (error) {
                        console.error("Error fetching providers:", error);
                    }

                }
                else {
                    setNetworks({
                        ...fff
                        , networks: []
                    });
                    console.log(`WARNING no loaded networks for ${JSON.stringify(fff)}`);
                }
            }
            else {
                console.log(JSON.stringify(data));
                setAlertError({
                    title: data.name
                    , body: data.description.replace(/\n/g, "<br />")
                });
            }
        }

        console.log('calling useEffect ' + JSON.stringify({
            network_id
        }));

        if (service_type_id || (rateSheetInput.serviceType && rateSheetInput.serviceType['service_type_id'] !== null)) {
            do_fetch();
        }

    }, [handleAPIRequest, provider_id, network_id, service_type_id, rateSheetInput]);

    useEffect(() => {
        fetchNetworks();
    }, [fetchNetworks]);

    // ************************************************************************
    // Rate sheet PDF
    //
    // The contract PDF is fetched as a blob (rather than pointed to
    // directly via <iframe src="/api/v1/...">) because every API call goes
    // through handleAPIRequest, which attaches the Bearer auth token - a
    // plain iframe/embed src can't carry that header. Fetching as a blob
    // and turning it into an object URL keeps auth working exactly the
    // same way it does for every other request in this app.

    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    useEffect(() => {

        // Revoke whichever object URL was previously in state whenever we
        // fetch a new one, or when this component unmounts - object URLs
        // aren't garbage collected on their own and will otherwise leak
        // memory for as long as the tab stays open.
        let objectUrlToRevoke = null;

        const do_fetch_pdf = async () => {

            setPdfLoading(true);
            setPdfUrl(null);

            const fetch_url = `/api/v1/rate_sheet_pdf/${provider_id}/${network_id}/${service_type_id}`;

            console.log('fetching rate sheet pdf: ' + fetch_url);

            const res = await handleAPIRequest(fetch_url);

            if (res.status === 200) {
                const blob = await res.blob();
                const objectUrl = URL.createObjectURL(blob);
                objectUrlToRevoke = objectUrl;
                setPdfUrl(objectUrl);
            }
            else {
                let data = {};
                try {
                    data = await res.json();
                } catch (error) {
                    // response wasn't JSON - fall through to the generic message below
                }
                console.log(JSON.stringify(data));
                setAlertError({
                    title: data.name || 'Unable to load contract'
                    , body: (data.description || 'No contract PDF could be found for this provider and service type.').replace(/\n/g, "<br />")
                });
            }

            setPdfLoading(false);
        }

        if (provider_id && network_id && service_type_id) {
            do_fetch_pdf();
        }

        return () => {
            if (objectUrlToRevoke) {
                URL.revokeObjectURL(objectUrlToRevoke);
            }
        };

    }, [handleAPIRequest, provider_id, network_id, service_type_id]);

    // ************************************************************************
    // Formatters

    const { currencyFormatter, percentageFormatter } = useFormatters();

    return (

        <>

            <Alert show={!!alertError} variant="danger">
                <Alert.Heading>{alertError ? alertError.title : ''}</Alert.Heading>
                <p dangerouslySetInnerHTML={alertError ? { __html: alertError.body } : { __html: '' }}></p>
            </Alert>

            {(network_id && networks.length > 0) ? (

                <div className='bg-light mt-4 mb-4 p-3'>

                    <Row className='mb-3 d-print-none' >
                        <a href={`/rate_sheets`}>&lt; Rate Sheet Search</a>
                    </Row>

                    {networks.map((n, n_idx) => (
                        <>

                            <Row className='mb-3'>
                                <h1>{n['provider']}</h1>
                                <h2>{n['network']}</h2>
                                <h3>{n['service_type']}</h3>
                                &nbsp;&nbsp;
                                <hr></hr>
                                &nbsp;&nbsp;
                            </Row>

                            <Row className='mb-3'>
                                {pdfLoading ? (
                                    <p>Loading contract...</p>
                                ) : pdfUrl ? (
                                    <>
                                        <iframe
                                            src={pdfUrl}
                                            title={`${n['provider']} ${n['service_type']} contract`}
                                            style={{ width: '100%', height: '80vh', border: 'none' }}
                                        />
                                        <p className='mt-2'>
                                            <a href={pdfUrl} target="_blank" rel="noreferrer">Open contract in a new tab</a>
                                        </p>
                                    </>
                                ) : (
                                    <p>No contract PDF is available for this provider and service type.</p>
                                )}
                            </Row>

                        </>
                    ))}

                </div>

            ) : (

                <>
                    <div className='bg-light mt-4 mb-4 p-3'>
                        <Row className='mb-3'>
                            <div className="col-md-6">
                                <h1>Rate Sheets</h1>
                            </div>
                        </Row>
                    </div>


                    <div className='bg-light mt-4 mb-4 p-3'>
                        <Row>
                            <div className="col-md-6">
                                <h2>Service Type</h2>
                            </div>

                            <div className="col-md-6 text-end align-middle">
                                <Button id="rate_sheet_reset_button" onClick={handleOnRateSheetReset} >Clear</Button>
                            </div>
                        </Row>

                        <Row>
                            <SimulationServiceTypeSelect
                                element_id="serviceType"
                                handleAPIRequest={handleAPIRequest}
                                simulationInput={rateSheetInput}
                                setAdultPediatric={setAdultPediatric}
                                setServiceTypeCategory={setServiceTypeCategory}
                                setServiceType={setServiceType}
                            />
                        </Row>
                    </div>


                    <div className='bg-light mt-4 mb-4 p-3'>

                        <Row className='mb-3'>
                            <div className="input-group">
                                <span className="input-group-text">Search</span>
                                <input type="text" className="form-control" value={networksTextFilter} onChange={(e) => setNetworksTextFilter(e.target.value)} id={'rate_sheets_textFilter'} />
                            </div>
                        </Row>


                        <ul id="rate_sheet_list">
                            {networks
                                .map((n) => {

                                    n['search_found'] = 0;

                                    if (networksTextFilter === '') {
                                        n['search_found'] = 1;
                                        return n;
                                    }

                                    let filters = networksTextFilter.split(' ');

                                    for (var i = 0; i < filters.length; i++) {

                                        if (filters[i] === '')
                                            continue;

                                        let re = new RegExp(filters[i].replace(/[\!\#\$\%\^\&\*\)\(\+\=\.\<\>\{\}\[\]\:\;\'\"\|\~\`\_\-]/g, '\\$&'), 'i');
                                        const result = n['name_full'].match(re);
                                        if (result)
                                            n['search_found'] += 1;
                                    }

                                    return n;

                                })
                                .filter((n) => n['search_found'] > 0)
                                .sort((a, b) => {
                                    if (a['search_found'] !== b['search_found']) {
                                        return a['search_found'] > b['search_found'] ? -1 : 1;
                                    }
                                    return a['name_full'] > b['name_full'] ? 1 : -1;
                                })
                                .map((n, n_idx) => (
                                    <li key={`${n['network_id']}_${n_idx}`}>
                                        <ul><a href={`/rate_sheets/${n['provider_id']}/${n['network_id']}/${n['service_type_id']}`}>{n['name_full']}</a></ul>
                                    </li>
                                ))}
                        </ul>

                    </div>

                </>
            )}

        </>

    )

}